import { useState } from "react";
import { Button, Flex, Popover, Typography } from "antd";
import { Ear, Users, VolumeX, Wand2, type LucideIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { AudioMode } from "./audioMode";

export const AUDIO_MODE_UI: Record<AudioMode, { icon: LucideIcon; color: string; label: string; hint: string }> = {
  auto: { icon: Wand2, color: "#2251ff", label: "proctoring.audioAuto", hint: "proctoring.audioAutoHint" },
  open: { icon: Users, color: "#30a46c", label: "proctoring.audioOpen", hint: "proctoring.audioOpenHint" },
  listen: { icon: Ear, color: "#d4b106", label: "proctoring.audioListen", hint: "proctoring.audioListenHint" },
  muted: { icon: VolumeX, color: "#8c8c8c", label: "proctoring.audioMuted", hint: "proctoring.audioMutedHint" },
};

interface Props {
  mode: AudioMode;
  wholeRoom: boolean;
  loading: boolean;
  disabled: boolean;
  iconOnly: boolean;
  onChange: (mode: AudioMode) => void;
  getPopupContainer?: () => HTMLElement;
}

/// One button showing who hears whom; it opens the four modes, each explained in one sentence.
export const AudioModeControl = ({ mode, wholeRoom, loading, disabled, iconOnly, onChange, getPopupContainer }: Props) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const current = AUDIO_MODE_UI[mode];
  const Icon = current.icon;

  const content = (
    <Flex vertical gap={6} style={{ width: 320, maxWidth: "80vw" }} data-testid="audio-mode-options">
      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
        {wholeRoom ? t("proctoring.audioScopeAll") : t("proctoring.audioScopeMine")}
      </Typography.Text>
      {(Object.keys(AUDIO_MODE_UI) as AudioMode[]).map((m) => {
        const ui = AUDIO_MODE_UI[m];
        const OptionIcon = ui.icon;
        const selected = m === mode;
        return (
          <button
            key={m}
            type="button"
            onClick={() => {
              setOpen(false);
              if (!selected) onChange(m);
            }}
            data-testid={`audio-mode-${m}`}
            aria-pressed={selected}
            style={{
              display: "flex",
              gap: 10,
              alignItems: "flex-start",
              textAlign: "left",
              padding: "8px 10px",
              borderRadius: 8,
              cursor: "pointer",
              border: `1px solid ${selected ? ui.color : "#303030"}`,
              background: selected ? `${ui.color}22` : "transparent",
              color: "inherit",
              minHeight: 44,
            }}
          >
            <span style={{ color: ui.color, flex: "none", marginTop: 2 }}>
              <OptionIcon size={18} />
            </span>
            <span>
              <Typography.Text strong style={{ display: "block" }}>
                {t(ui.label)}
              </Typography.Text>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {t(ui.hint)}
              </Typography.Text>
            </span>
          </button>
        );
      })}
    </Flex>
  );

  return (
    <Popover
      trigger="click"
      open={open}
      onOpenChange={(v) => !disabled && setOpen(v)}
      content={content}
      title={t("proctoring.audio")}
      getPopupContainer={getPopupContainer}
    >
      <Button
        icon={<Icon size={16} color={current.color} />}
        loading={loading}
        disabled={disabled}
        data-testid="audio-mode"
        data-mode={mode}
        style={{ borderColor: current.color }}
      >
        {!iconOnly && `${t("proctoring.audio")}: ${t(current.label)}`}
      </Button>
    </Popover>
  );
};
